/**
 * DOM chrome: the piece tray, HUD readouts, score popovers, toasts, and the
 * home / how-to-play / game-over screens.
 *
 * All explanatory copy lives in the how-to-play panel. The playfield carries no
 * instructions of its own — a permanent line of help text under the pyramid is
 * clutter once you know the game, and the `?` is always there for when you
 * don't.
 */

export class UI {
  constructor(root = document) {
    this.root = root;
    this.slots = [...root.querySelectorAll('.tray-slot')];
    this.scoreEl = root.getElementById('score');
    this.bestEl = root.getElementById('best');
    this.toastEl = root.getElementById('toast');

    this.homeEl = root.getElementById('home');
    this.homeBestEl = root.getElementById('home-best');
    this.playEl = root.getElementById('play');
    this.homeHelpEl = root.getElementById('home-help');
    this.#watchLockup(root);

    this.howToEl = root.getElementById('howto');
    this.howToPanelEl = root.querySelector('.panel.howto');
    this.helpEl = root.getElementById('help');
    this.howToCloseEl = root.getElementById('howto-close');

    this.soundEl = root.getElementById('sound');

    this.gameOverEl = root.getElementById('gameover');
    this.finalScoreEl = root.getElementById('final-score');
    this.recordEl = root.getElementById('record');
    this.gameOverBestEl = root.getElementById('gameover-best');
    this.restartEl = root.getElementById('restart');

    this.toastTimer = null;
    this.renderedScore = 0;
  }

  /**
   * Fall back to the drawn lockup if the artwork does not load.
   *
   * `error` alone is not enough. A static host that answers a missing path with
   * `index.html` — which is what this game's own Worker does, and what most SPA
   * hosts do — returns 200 and a body the decoder rejects, so the failure can
   * arrive either as an error event or as a load with no intrinsic size. Both
   * are checked, and `complete` covers the image that resolved from cache
   * before this constructor ever ran.
   */
  #watchLockup(root) {
    const art = root.getElementById('home-art');
    const drawn = root.getElementById('home-drawn');
    if (!art || !drawn) return;

    const decide = () => {
      const broken = !art.naturalWidth;
      art.hidden = broken;
      drawn.hidden = !broken;
    };

    if (art.complete) decide();
    art.addEventListener('load', decide, { once: true });
    art.addEventListener('error', decide, { once: true });
  }

  /* ----------------------------------------------------------------- screens */

  /**
   * Which screen is up. Written to the body so CSS can hide the HUD and tray on
   * the home screen without the caller having to know what that involves.
   *
   * @param {'home'|'playing'} phase
   */
  setPhase(phase) {
    (this.root.body ?? this.root.documentElement).dataset.phase = phase;
    this.homeEl.hidden = phase !== 'home';
  }

  showHowTo() {
    this.howToEl.hidden = false;
    // Always from the top. And `preventScroll`, or focusing the button at the
    // bottom scrolls the panel down to it and the reader opens mid-sentence.
    this.howToPanelEl.scrollTop = 0;
    this.howToCloseEl.focus?.({ preventScroll: true });
  }

  hideHowTo() {
    this.howToEl.hidden = true;
  }

  get howToOpen() {
    return !this.howToEl.hidden;
  }

  /**
   * Show whether the sound is off.
   *
   * `aria-pressed` carries the state for a screen reader and drives both the
   * dimming and which half of the icon shows, so the three cannot disagree. The
   * label is the *current* state rather than what pressing would do — "Sound on"
   * on a button that is on — which is the convention for a toggle that reports
   * `pressed`, since the reader announces the pressed state itself and a label
   * saying "Mute" would contradict it.
   */
  setMuted(muted) {
    if (!this.soundEl) return;
    this.soundEl.setAttribute('aria-pressed', String(muted));
    const label = muted ? 'Sound off' : 'Sound on';
    this.soundEl.setAttribute('aria-label', label);
    this.soundEl.title = label;
  }

  /**
   * Repaint the tray. The pieces themselves are drawn as stones in the 3D
   * canvas — these slots are the hit targets and the layout, nothing visible.
   *
   * @param {object} state
   * @param {number|null} heldSlot slot currently being dragged
   * @param {boolean[]} dead slots with no legal placement left
   */
  renderTray(state, heldSlot, dead = []) {
    this.slots.forEach((el, slot) => {
      const entry = state.tray[slot];
      el.classList.toggle('empty', !entry);
      el.classList.toggle('active', heldSlot === slot && !!entry);
      el.classList.toggle('dead', !!dead[slot]);
      if (entry) el.dataset.piece = entry.key;
    });
  }

  /** Slot rectangles relative to the canvas, for placing the 3D stones. */
  slotRects(canvas) {
    const origin = canvas.getBoundingClientRect();
    return this.slots.map((el) => {
      const r = el.getBoundingClientRect();
      return { left: r.left - origin.left, top: r.top - origin.top, width: r.width, height: r.height };
    });
  }

  setScore(score) {
    // Roll the number up so a big clear feels like one.
    if (this.renderedScore === score) return;
    cancelAnimationFrame(this.scoreRaf);
    const from = this.renderedScore;
    const started = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 320);
      const eased = 1 - (1 - t) ** 3;
      const value = Math.round(from + (score - from) * eased);
      this.scoreEl.textContent = value.toLocaleString();
      if (t < 1) this.scoreRaf = requestAnimationFrame(tick);
    };
    this.renderedScore = score;
    this.scoreRaf = requestAnimationFrame(tick);
  }

  /** The high score, shown in the HUD and on the home screen. */
  /**
   * The high score, in the HUD and on the title screen.
   *
   * The title screen only carries it once there is one. A fresh install shows
   * the lockup exactly as designed — emblem, title, Play, wordmark, nothing
   * else — and a returning player still gets their score back rather than
   * losing it to the redesign.
   */
  setBest(best) {
    const text = best.toLocaleString();
    this.bestEl.textContent = text;
    if (!this.homeBestEl) return;
    this.homeBestEl.textContent = text;
    const line = this.homeBestEl.closest('.home-best');
    if (line) line.hidden = best <= 0;
  }

  toast(message) {
    if (!message) return;
    this.toastEl.textContent = message;
    this.toastEl.classList.add('show');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toastEl.classList.remove('show'), 1100);
  }

  /**
   * A score label that rises out of a formation and fades.
   *
   * Positioned absolutely over the canvas rather than drawn in it: text stays
   * crisp and the styling stays CSS's job. It removes itself when its animation
   * ends, so nothing has to track it.
   *
   * Its lifetime is tied to the beat it was shown on, running a little past it
   * so the label can be read — long enough to linger, short enough that a combo
   * does not end up with a stack of them on screen.
   *
   * @param {{label: string, points: number, multiplier?: number}} score
   * @param {{x: number, y: number}} at viewport coordinates
   * @param {number} stepMs the beat this popover belongs to
   */
  scorePopover(score, at, stepMs = 520) {
    const el = this.root.createElement('div');
    el.className = 'score-pop';
    el.style.left = `${at.x}px`;
    el.style.top = `${at.y}px`;
    // Just under two beats, so the one before is on its way out as the next
    // arrives — a cascade, never a stack.
    el.style.animationDuration = `${Math.max(360, Math.round(stepMs * 1.9))}ms`;

    const name = this.root.createElement('span');
    name.className = 'score-pop-label';
    name.textContent = score.label;

    const value = this.root.createElement('span');
    value.className = 'score-pop-points';
    value.textContent = `+${score.points.toLocaleString()}`;

    el.append(name, value);

    if (score.multiplier && score.multiplier > 1) {
      const combo = this.root.createElement('span');
      combo.className = 'score-pop-combo';
      combo.textContent = `${score.multiplier}×`;
      el.append(combo);
    }

    (this.root.body ?? this.root).append(el);
    el.addEventListener('animationend', () => el.remove());
    return el;
  }

  /** Clear any popovers still on screen, for a restart. */
  clearPopovers() {
    for (const el of this.root.querySelectorAll('.score-pop')) el.remove();
  }

  /**
   * @param {number} score the score just finished on
   * @param {number} best the high score, after this game
   * @param {boolean} record whether this game set it
   */
  showGameOver(score, best, record) {
    this.finalScoreEl.textContent = score.toLocaleString();
    this.gameOverBestEl.textContent = best.toLocaleString();
    this.recordEl.hidden = !record;
    // A record makes the final score the headline; otherwise the best stands
    // apart from it as the thing still to beat.
    this.gameOverBestEl.parentElement.hidden = record;
    this.gameOverEl.hidden = false;
  }

  hideGameOver() {
    this.gameOverEl.hidden = true;
  }

  resetScore() {
    this.renderedScore = 0;
    this.scoreEl.textContent = '0';
  }
}

/**
 * Headline for a multi-formation move.
 *
 * Only combos get one. A single formation announces itself in the popover that
 * rises out of it, and a toast saying the same thing again would just compete
 * with it.
 */
export function describeResult(result) {
  if (!result || result.combo <= 1) return '';
  return `${result.combo}× COMBO · ${result.multiplier}×`;
}
